import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SearchIcon from '@mui/icons-material/Search';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import { RAG_CONFIG } from '../config';

const AdvancedRAGTest = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchType, setSearchType] = useState('advanced');
  const [searchConfig, setSearchConfig] = useState({
    top_k: 5,
    similarity_threshold: 0.7,
    use_hybrid_search: true,
    use_semantic_reranking: true,
    group_by_source: true
  });
  const [suggestions, setSuggestions] = useState([]);
  const [analytics, setAnalytics] = useState(null);

  // Search suggestions
  useEffect(() => {
    if (query.length >= 2) {
      getSearchSuggestions();
    } else {
      setSuggestions([]);
    }
  }, [query]);

  const getSearchSuggestions = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/v1/rag/search/suggestions?query=${encodeURIComponent(query)}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${process.env.REACT_APP_API_KEY || 'test-key'}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setSuggestions(data.suggestions || []);
      }
    } catch (error) {
      console.error('Failed to get suggestions:', error);
    }
  };

  const handleSearch = async () => {
    if (!query.trim()) return;

    setLoading(true);
    setError('');
    setResults([]);

    try {
      let response;
      
      if (searchType === 'advanced') {
        // Advanced search
        response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/v1/rag/search/advanced`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${process.env.REACT_APP_API_KEY || 'test-key'}`
          },
          body: JSON.stringify({
            query: query,
            top_k: searchConfig.top_k,
            similarity_threshold: searchConfig.similarity_threshold,
            use_hybrid_search: searchConfig.use_hybrid_search,
            use_semantic_reranking: searchConfig.use_semantic_reranking,
            group_by_source: searchConfig.group_by_source
          })
        });
      } else {
        // Legacy search
        response = await fetch(
          `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/v1/rag/search?query=${encodeURIComponent(query)}&limit=${searchConfig.top_k}`,
          {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${process.env.REACT_APP_API_KEY || 'test-key'}`
            }
          }
        );
      }

      if (!response.ok) {
        throw new Error(`Search failed: ${response.statusText}`);
      }

      const data = await response.json();
      
      if (data.success) {
        setResults(data.results || []);
      } else {
        setError(data.error || 'Search failed');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getAnalytics = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/v1/rag/search/analytics`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${process.env.REACT_APP_API_KEY || 'test-key'}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setAnalytics(data.analytics);
      }
    } catch (error) {
      console.error('Failed to get analytics:', error);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setQuery(suggestion);
    setSuggestions([]);
  };

  const renderResults = () => {
    if (Array.isArray(results)) {
      // Legacy format - flat results
      return results.map((result, index) => (
        <motion.div
          key={result.id || index}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.1 }}
          className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-md border border-gray-200 dark:border-gray-700"
        >
          <div className="flex justify-between items-start mb-2">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-purple-600 dark:text-purple-400">
                {result.subject} - Class {result.class_level}
              </span>
              {result.chapter && (
                <span className="text-xs bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-2 py-1 rounded">
                  {result.chapter}
                </span>
              )}
            </div>
            <span className="text-xs text-gray-500">
              Score: {(result.similarity * 100).toFixed(1)}%
            </span>
          </div>
          <p className="text-gray-700 dark:text-gray-300 text-sm mb-2">
            {result.content}
          </p>
          <div className="text-xs text-gray-500">
            Source: {result.file_name} (Chunk {result.chunk_index + 1}/{result.total_chunks})
          </div>
        </motion.div>
      ));
    } else if (typeof results === 'object') {
      // Advanced format - grouped results
      return Object.entries(results).map(([sourceKey, sourceResults], sourceIndex) => (
        <motion.div
          key={sourceKey}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: sourceIndex * 0.1 }}
          className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-md border border-gray-200 dark:border-gray-700 mb-4"
        >
          <h4 className="font-semibold text-gray-900 dark:text-white mb-3 border-b border-gray-200 dark:border-gray-700 pb-2">
            📄 {sourceKey} ({sourceResults.length} chunks)
          </h4>
          <div className="space-y-3">
            {sourceResults.map((result, index) => (
              <div key={result.id || index} className="pl-4 border-l-2 border-purple-200 dark:border-purple-800">
                <div className="flex justify-between items-start mb-1">
                  <span className="text-xs text-gray-500">
                    Chunk {result.chunk_index + 1}/{result.total_chunks}
                  </span>
                  <span className="text-xs text-gray-500">
                    Score: {(result.similarity * 100).toFixed(1)}%
                  </span>
                </div>
                <p className="text-gray-700 dark:text-gray-300 text-sm">
                  {result.content}
                </p>
              </div>
            ))}
          </div>
        </motion.div>
      ));
    }
    return null;
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6">
      <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
        🔍 Advanced RAG Search Test
      </h3>
      
      <div className="space-y-6">
        {/* Search Configuration */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Search Type
            </label>
            <select
              value={searchType}
              onChange={(e) => setSearchType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="advanced">Advanced Search</option>
              <option value="legacy">Legacy Search</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Results Limit
            </label>
            <input
              type="number"
              value={searchConfig.top_k}
              onChange={(e) => setSearchConfig(prev => ({ ...prev, top_k: parseInt(e.target.value) }))}
              min="1"
              max="20"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Advanced Options */}
        {searchType === 'advanced' && (
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
            <h4 className="font-medium text-gray-900 dark:text-white mb-3">Advanced Options</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="hybrid_search"
                  checked={searchConfig.use_hybrid_search}
                  onChange={(e) => setSearchConfig(prev => ({ ...prev, use_hybrid_search: e.target.checked }))}
                  className="mr-2"
                />
                <label htmlFor="hybrid_search" className="text-sm text-gray-700 dark:text-gray-300">
                  Hybrid Search
                </label>
              </div>
              
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="semantic_reranking"
                  checked={searchConfig.use_semantic_reranking}
                  onChange={(e) => setSearchConfig(prev => ({ ...prev, use_semantic_reranking: e.target.checked }))}
                  className="mr-2"
                />
                <label htmlFor="semantic_reranking" className="text-sm text-gray-700 dark:text-gray-300">
                  Semantic Reranking
                </label>
              </div>
              
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="group_by_source"
                  checked={searchConfig.group_by_source}
                  onChange={(e) => setSearchConfig(prev => ({ ...prev, group_by_source: e.target.checked }))}
                  className="mr-2"
                />
                <label htmlFor="group_by_source" className="text-sm text-gray-700 dark:text-gray-300">
                  Group by Source
                </label>
              </div>
            </div>
            
            <div className="mt-3">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Similarity Threshold: {searchConfig.similarity_threshold}
              </label>
              <input
                type="range"
                min="0.1"
                max="1.0"
                step="0.1"
                value={searchConfig.similarity_threshold}
                onChange={(e) => setSearchConfig(prev => ({ ...prev, similarity_threshold: parseFloat(e.target.value) }))}
                className="w-full"
              />
            </div>
          </div>
        )}

        {/* Search Input */}
        <div className="relative">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Search Query
          </label>
          <div className="relative">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Enter your search query..."
              className="w-full px-4 py-3 pl-12 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
            <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          </div>
          
          {/* Search Suggestions */}
          {suggestions.length > 0 && (
            <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg">
              {suggestions.map((suggestion, index) => (
                <button
                  key={index}
                  onClick={() => handleSuggestionClick(suggestion)}
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 first:rounded-t-lg last:rounded-b-lg"
                >
                  {suggestion}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Search Button */}
        <button
          onClick={handleSearch}
          disabled={!query.trim() || loading}
          className="w-full px-4 py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 disabled:bg-gray-400 text-white rounded-lg transition-all duration-200 flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              Searching...
            </>
          ) : (
            <>
              <AutoAwesomeIcon />
              {searchType === 'advanced' ? 'Advanced Search' : 'Search'}
            </>
          )}
        </button>

        {/* Analytics Button */}
        <button
          onClick={getAnalytics}
          className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center justify-center gap-2"
        >
          <TrendingUpIcon />
          Get Search Analytics
        </button>

        {/* Analytics Display */}
        {analytics && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4"
          >
            <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-2">Search Analytics</h4>
            <div className="text-sm text-blue-700 dark:text-blue-300">
              <p>Total Searches: {analytics.total_searches}</p>
              <p>Success Rate: {(analytics.search_success_rate * 100).toFixed(1)}%</p>
              <p>Average Results: {analytics.average_results}</p>
            </div>
          </motion.div>
        )}

        {/* Error Display */}
        {error && (
          <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
          </div>
        )}

        {/* Results Display */}
        {results.length > 0 && (
          <div>
            <h4 className="font-medium text-gray-900 dark:text-white mb-3">
              Search Results ({Array.isArray(results) ? results.length : Object.keys(results).length} sources)
            </h4>
            <div className="space-y-3">
              {renderResults()}
            </div>
          </div>
        )}

        {/* No Results */}
        {!loading && !error && results.length === 0 && query && (
          <div className="text-center py-8">
            <LightbulbIcon className="text-4xl text-gray-400 mx-auto mb-2" />
            <p className="text-gray-500 dark:text-gray-400">No results found. Try a different query or check your document uploads.</p>
          </div>
        )}

        {/* Configuration Info */}
        <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
          <h5 className="font-medium text-purple-900 dark:text-purple-100 mb-2">Search Configuration</h5>
          <div className="text-xs text-purple-700 dark:text-purple-300 space-y-1">
            <p><strong>Embedding Model:</strong> {RAG_CONFIG.embedding_model}</p>
            <p><strong>Generation Model:</strong> {RAG_CONFIG.generation_model}</p>
            <p><strong>Search Type:</strong> {searchType}</p>
            <p><strong>Results Limit:</strong> {searchConfig.top_k}</p>
            <p><strong>Similarity Threshold:</strong> {searchConfig.similarity_threshold}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdvancedRAGTest; 